import { useId, useRef, useState, type FormEvent, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import "../../../auth/auth.css";
import { Modal } from "../../../auth/Modal.tsx";
import { RevealInput } from "../../../auth/RevealInput.tsx";
import { useServiceAdmin } from "./use-service-admin.ts";
import { StatusBadge, type StatusLevel } from "../../../components/index.ts";
import { OtpInput } from "../../../components/OtpInput.tsx";
import { formatDateTime } from "../../../lib/datetime.ts";
import {
  OUTBOX_PATH,
  RECEIVER_PATH,
  SIGNALS_EVENT_LABEL,
  SIGNALS_EVENT_TYPES,
  SIGNALS_QUERY_ROOT,
  STREAMS_PATH,
  signalsMessage,
  signalsWrite,
  streamPath,
  type OutboxResponse,
  type ReceiverResponse,
  type ReceiverView,
  type SignalsEventType,
  type SignalsStream,
  type StreamsResponse,
} from "./signals-client.ts";
/**
 * The "Signals" tab of the AIS console (G3 #139): Shared Signals out and in. Everything is Off until a
 * stream or a receiver exists (the transmitter and receiver are also off by deployment flag). Team
 * administrators get the controls; everyone else reads. The buttons only mirror what the server will
 * allow: the BFF and the AIS enforce the admin role, the reason, the strict step-up and the tenant.
 */
const STATUS_LEVEL: Readonly<Record<SignalsStream["status"], StatusLevel>> = {
  enabled: "healthy",
  paused: "warn",
  paused_by_failure: "error",
};
const STATUS_LABEL: Readonly<Record<SignalsStream["status"], string>> = {
  enabled: "Enabled",
  paused: "Paused",
  paused_by_failure: "Paused after repeated failures",
};
type ModalState =
  | { readonly kind: "create" }
  | { readonly kind: "toggle" | "delete"; readonly stream: SignalsStream }
  | { readonly kind: "receiver" }
  | null;
function countFor(outbox: OutboxResponse | undefined, streamId: string, status: string): number {
  return (outbox?.counts ?? []).filter((row) => row.streamId === streamId && row.status === status).reduce((sum, row) => sum + row.count, 0);
}
function lastDelivery(outbox: OutboxResponse | undefined, streamId: string): string {
  const row = (outbox?.recent ?? [])
    .filter((r) => r.streamId === streamId && r.status === "delivered" && r.finishedAt !== null)
    .sort((a, b) => (b.finishedAt ?? 0) - (a.finishedAt ?? 0))[0];
  return row?.finishedAt == null ? "Never" : formatDateTime(row.finishedAt);
}
export function SignalsPanel(props: { readonly isAdmin: boolean }): ReactNode {
  const { isAdmin } = props;
  const streams = useServiceAdmin<StreamsResponse>("ais", STREAMS_PATH, ["ssf", "streams"]);
  const outbox = useServiceAdmin<OutboxResponse>("ais", OUTBOX_PATH, ["ssf", "outbox"]);
  const receiver = useServiceAdmin<ReceiverResponse>("ais", RECEIVER_PATH, ["ssf", "receiver"]);
  const queryClient = useQueryClient();
  const [modal, setModal] = useState<ModalState>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const triggerRef = useRef<HTMLElement | null>(null);
  const refresh = async (message: string): Promise<void> => {
    setModal(null);
    setNotice(message);
    await queryClient.invalidateQueries({ queryKey: SIGNALS_QUERY_ROOT });
  };
  const open = (next: NonNullable<ModalState>) => (e: { currentTarget: HTMLElement }): void => {
    triggerRef.current = e.currentTarget;
    setNotice(null);
    setModal(next);
  };
  const list = streams.data?.streams ?? [];
  const transmitterOn = streams.data?.transmitter_enabled === true;
  return (
    <div className="svc-admin svc-admin--full svc-signals">
      {notice !== null && <p className="svc-admin__muted" role="status">{notice}</p>}
      <section className="svc-adm2__card" aria-label="Outbound streams">
        <div className="svc-adm2__card-head">
          <h3 className="svc-admin__h">Outbound streams</h3>
          {isAdmin && <button type="button" className="svc-admin__btn" onClick={open({ kind: "create" })}>Add stream</button>}
        </div>
        {streams.isPending ? (
          <p className="svc-admin__muted" role="status">Loading streams…</p>
        ) : (
          <p className="svc-admin__muted">
            {list.length === 0 ? "Off. No stream is configured, so no signals are sent." : `${list.length} stream${list.length === 1 ? "" : "s"} configured.`}{" "}
            {!transmitterOn && "Sending is also disabled on this deployment."}
          </p>
        )}
        {streams.isError && <p className="svc-admin__err" role="alert">The streams could not be loaded.</p>}
        {list.length > 0 && (
          <table className="svc-admin__table" aria-label="Streams">
            <thead>
              <tr>
                <th scope="col">Receiver</th>
                <th scope="col">Status</th>
                <th scope="col">Events</th>
                <th scope="col">Pending</th>
                <th scope="col">Dead</th>
                <th scope="col">Last delivery</th>
                {isAdmin && <th scope="col"><span className="svc-admin__sr">Actions</span></th>}
              </tr>
            </thead>
            <tbody>
              {list.map((stream) => (
                <tr key={stream.stream_id}>
                  <th scope="row">{stream.receiver_host ?? stream.receiver_url}</th>
                  <td><StatusBadge level={STATUS_LEVEL[stream.status]} label={STATUS_LABEL[stream.status]} /></td>
                  <td>{stream.event_types.map((t) => SIGNALS_EVENT_LABEL[t as SignalsEventType] ?? t).join(", ")}</td>
                  <td>{countFor(outbox.data, stream.stream_id, "pending")}</td>
                  <td>{countFor(outbox.data, stream.stream_id, "dead")}</td>
                  <td>{lastDelivery(outbox.data, stream.stream_id)}</td>
                  {isAdmin && (
                    <td>
                      <div className="svc-admin__actions">
                        <button type="button" className="svc-admin__btn" onClick={open({ kind: "toggle", stream })}>
                          {stream.status === "enabled" ? "Pause" : "Resume"}
                          <span className="svc-admin__sr"> {stream.receiver_host ?? "stream"}</span>
                        </button>
                        <button type="button" className="svc-admin__btn svc-admin__btn--danger" onClick={open({ kind: "delete", stream })}>
                          Delete<span className="svc-admin__sr"> {stream.receiver_host ?? "stream"}</span>
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {streams.data !== undefined && (
          <p className="svc-admin__muted">
            Receivers should accept an <code>iat</code> up to {Math.round(streams.data.receiver_guidance.accept_iat_max_age_s / 3600)} hours old and
            dedupe on <code>jti</code>: a failed delivery is re-sent byte for byte.
          </p>
        )}
      </section>
      <section className="svc-adm2__card" aria-label="Outbox health">
        <div className="svc-adm2__card-head"><h3 className="svc-admin__h">Outbox health</h3></div>
        {outbox.isError && <p className="svc-admin__err" role="alert">The outbox could not be loaded.</p>}
        {outbox.data !== undefined && (
          <>
            <ul className="svc-admin__list" aria-label="Outbox counts">
              {["pending", "delivered", "failed", "dead"].map((status) => (
                <li key={status}>
                  {status}: {(outbox.data?.counts ?? []).filter((row) => row.status === status).reduce((sum, row) => sum + row.count, 0)}
                </li>
              ))}
            </ul>
            {outbox.data.recent.length === 0 ? (
              <p className="svc-admin__muted">No signals have been queued.</p>
            ) : (
              <table className="svc-admin__table" aria-label="Recent signals">
                <thead>
                  <tr><th scope="col">Event</th><th scope="col">Status</th><th scope="col">Attempts</th><th scope="col">Last result</th></tr>
                </thead>
                <tbody>
                  {outbox.data.recent.slice(0, 10).map((row) => (
                    <tr key={row.outboxId}>
                      <th scope="row">{SIGNALS_EVENT_LABEL[row.eventType as SignalsEventType] ?? row.eventType}</th>
                      <td>{row.status}</td>
                      <td>{row.attempts}</td>
                      <td>{row.lastError ?? (row.lastStatus === null ? "—" : String(row.lastStatus))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}
      </section>
      <ReceiverCard
        query={receiver.data}
        loading={receiver.isPending}
        failed={receiver.isError}
        isAdmin={isAdmin}
        onConfigure={open({ kind: "receiver" })}
      />
      {/* Mounted only while open, so each opening starts with an empty form. */}
      {modal?.kind === "create" && <CreateStreamModal onClose={() => setModal(null)} onDone={() => refresh("Stream added.")} />}
      {modal?.kind === "toggle" && (
        <ToggleStreamModal
          stream={modal.stream}
          onClose={() => setModal(null)}
          onDone={() => refresh(modal.stream.status === "enabled" ? "Stream paused." : "Stream resumed.")}
        />
      )}
      {modal?.kind === "delete" && <DeleteStreamModal stream={modal.stream} onClose={() => setModal(null)} onDone={() => refresh("Stream deleted.")} />}
      {modal?.kind === "receiver" && (
        <ReceiverModal current={receiver.data?.receiver ?? null} onClose={() => setModal(null)} onDone={(message) => refresh(message)} />
      )}
    </div>
  );
}
function ReceiverCard(props: {
  readonly query: ReceiverResponse | undefined;
  readonly loading: boolean;
  readonly failed: boolean;
  readonly isAdmin: boolean;
  readonly onConfigure: (e: { currentTarget: HTMLElement }) => void;
}): ReactNode {
  const receiver = props.query?.receiver ?? null;
  return (
    <section className="svc-adm2__card" aria-label="Inbound receiver">
      <div className="svc-adm2__card-head">
        <h3 className="svc-admin__h">Inbound receiver</h3>
        {props.isAdmin && (
          <button type="button" className="svc-admin__btn" onClick={props.onConfigure}>
            {receiver === null ? "Configure receiver" : "Edit receiver"}
          </button>
        )}
      </div>
      {props.failed && <p className="svc-admin__err" role="alert">The receiver could not be loaded.</p>}
      {!props.loading && receiver === null && !props.failed && (
        <p className="svc-admin__muted">Off. No receiver is configured, so inbound signals are not accepted.</p>
      )}
      {receiver !== null && (
        <dl className="svc-admin__facts">
          <dt>Status</dt>
          <dd>{receiver.enabled ? (props.query?.receiver_enabled === true ? "Enabled" : "Configured, but receiving is disabled on this deployment") : "Disabled"}</dd>
          <dt>Receive URL</dt>
          <dd><code>{receiver.receive_url}</code></dd>
          <dt>Issuer</dt>
          <dd>{receiver.issuer}</dd>
          <dt>Key set</dt>
          <dd>{receiver.jwks_uri}</dd>
          <dt>Audience</dt>
          <dd>{receiver.audience}</dd>
          <dt>Bearer</dt>
          <dd>{receiver.bearer_configured ? "Set (shown once when created or rotated)" : "Not set"}</dd>
        </dl>
      )}
    </section>
  );
}
/** Runs one signals write and maps a refusal to a plain-language message; `mfa_required` keeps the dialog open. */
function useSignalsSubmit() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = async <T,>(
    send: () => Promise<{ ok: true; body: T } | { ok: false; code: string }>,
    onOk: (body: T) => Promise<void>,
    clearCode: () => void,
  ): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const result = await send();
      if (result.ok) {
        await onOk(result.body);
        return;
      }
      if (result.code === "mfa_required") clearCode();
      setError(signalsMessage(result.code));
    } finally {
      setBusy(false);
    }
  };
  return { busy, error, run };
}
function ReasonAndCode(props: {
  readonly reason: string;
  readonly setReason: (v: string) => void;
  readonly code: string;
  readonly setCode: (v: string) => void;
  readonly error: string | null;
}): ReactNode {
  const reasonId = useId();
  const codeId = useId();
  return (
    <>
      <div className="svc-admin__field">
        <label htmlFor={reasonId}>Reason (required, recorded in the audit trail)</label>
        <textarea id={reasonId} value={props.reason} maxLength={256} rows={3} required onChange={(e) => props.setReason(e.target.value)} />
      </div>
      <div className="svc-admin__field">
        <label htmlFor={codeId}>Two-factor code</label>
        <OtpInput id={codeId} value={props.code} onChange={props.setCode} required align="start" />
      </div>
      {props.error !== null && <p className="svc-admin__err" role="alert">{props.error}</p>}
    </>
  );
}
function Footer(props: {
  readonly formId: string;
  readonly busy: boolean;
  readonly disabled: boolean;
  readonly label: string;
  readonly busyLabel: string;
  readonly danger?: boolean;
  readonly onClose: () => void;
}): ReactNode {
  return (
    <>
      <button type="button" className="svc-admin__btn" onClick={props.onClose} disabled={props.busy}>Cancel</button>
      <button
        type="submit"
        form={props.formId}
        className={props.danger === true ? "svc-admin__btn svc-admin__btn--danger" : "svc-admin__btn"}
        disabled={props.busy || props.disabled}
      >
        {props.busy ? props.busyLabel : props.label}
      </button>
    </>
  );
}
function CreateStreamModal(props: { readonly onClose: () => void; readonly onDone: () => Promise<void> }): ReactNode {
  const formId = useId();
  const urlId = useId();
  const audienceId = useId();
  const [url, setUrl] = useState("");
  const [audience, setAudience] = useState("");
  const [types, setTypes] = useState<ReadonlySet<SignalsEventType>>(new Set(SIGNALS_EVENT_TYPES));
  const [standard, setStandard] = useState(false);
  const [reason, setReason] = useState("");
  const [code, setCode] = useState("");
  const { busy, error, run } = useSignalsSubmit();
  const submit = (e: FormEvent): void => {
    e.preventDefault();
    void run(
      async () => {
        const r = await signalsWrite("POST", STREAMS_PATH, {
          receiver_url: url.trim(),
          ...(audience.trim() === "" ? {} : { audience: audience.trim() }),
          event_types: SIGNALS_EVENT_TYPES.filter((t) => types.has(t)),
          include_standard_events: standard,
          reason: reason.trim(),
          mfa_code: code,
        });
        return r.ok ? { ok: true as const, body: r.body } : { ok: false as const, code: r.code };
      },
      () => props.onDone(),
      () => setCode(""),
    );
  };
  const toggle = (type: SignalsEventType): void => {
    const next = new Set(types);
    if (next.has(type)) next.delete(type);
    else next.add(type);
    setTypes(next);
  };
  return (
    <Modal
      open
      onClose={busy ? () => undefined : props.onClose}
      title="Add stream"
      description="Send agent lifecycle signals to a receiver you control. Nothing is called when you save; the first delivery happens when an event occurs."
      footer={
        <Footer
          formId={formId}
          busy={busy}
          disabled={url.trim() === "" || types.size === 0 || reason.trim().length === 0 || code.length !== 6}
          label="Add stream"
          busyLabel="Adding…"
          onClose={props.onClose}
        />
      }
    >
      <form id={formId} className="svc-admin__form" onSubmit={submit} noValidate>
        <div className="svc-admin__field">
          <label htmlFor={urlId}>Receiver URL (https, public host)</label>
          <input id={urlId} type="url" value={url} required onChange={(e) => setUrl(e.target.value)} />
        </div>
        <div className="svc-admin__field">
          <label htmlFor={audienceId}>Audience (optional; defaults to the receiver&apos;s origin)</label>
          <input id={audienceId} type="text" value={audience} onChange={(e) => setAudience(e.target.value)} />
        </div>
        <fieldset className="svc-admin__field">
          <legend>Events to send</legend>
          {SIGNALS_EVENT_TYPES.map((type) => (
            <label key={type} className="svc-admin__check">
              <input type="checkbox" checked={types.has(type)} onChange={() => toggle(type)} /> {SIGNALS_EVENT_LABEL[type]}
            </label>
          ))}
        </fieldset>
        <label className="svc-admin__check">
          <input type="checkbox" checked={standard} onChange={(e) => setStandard(e.target.checked)} /> Also send the nearest standard CAEP/RISC event
        </label>
        <ReasonAndCode reason={reason} setReason={setReason} code={code} setCode={setCode} error={error} />
      </form>
    </Modal>
  );
}
function ToggleStreamModal(props: { readonly stream: SignalsStream; readonly onClose: () => void; readonly onDone: () => Promise<void> }): ReactNode {
  const formId = useId();
  const [reason, setReason] = useState("");
  const [code, setCode] = useState("");
  const { busy, error, run } = useSignalsSubmit();
  const pausing = props.stream.status === "enabled";
  const host = props.stream.receiver_host ?? "this receiver";
  const submit = (e: FormEvent): void => {
    e.preventDefault();
    void run(
      async () => {
        const r = await signalsWrite("PATCH", streamPath(props.stream.stream_id), {
          status: pausing ? "paused" : "enabled",
          reason: reason.trim(),
          mfa_code: code,
        });
        return r.ok ? { ok: true as const, body: r.body } : { ok: false as const, code: r.code };
      },
      () => props.onDone(),
      () => setCode(""),
    );
  };
  return (
    <Modal
      open
      onClose={busy ? () => undefined : props.onClose}
      title={pausing ? "Pause stream" : "Resume stream"}
      description={
        pausing
          ? `Pause sending to ${host}? Events that occur while it is paused are not queued for it.`
          : `Resume sending to ${host}? Its failure count starts again from zero.`
      }
      footer={
        <Footer
          formId={formId}
          busy={busy}
          disabled={reason.trim().length === 0 || code.length !== 6}
          label={pausing ? "Pause stream" : "Resume stream"}
          busyLabel="Saving…"
          onClose={props.onClose}
        />
      }
    >
      <form id={formId} className="svc-admin__form" onSubmit={submit} noValidate>
        <ReasonAndCode reason={reason} setReason={setReason} code={code} setCode={setCode} error={error} />
      </form>
    </Modal>
  );
}
function DeleteStreamModal(props: { readonly stream: SignalsStream; readonly onClose: () => void; readonly onDone: () => Promise<void> }): ReactNode {
  const formId = useId();
  const [reason, setReason] = useState("");
  const [code, setCode] = useState("");
  const { busy, error, run } = useSignalsSubmit();
  const submit = (e: FormEvent): void => {
    e.preventDefault();
    void run(
      async () => {
        const r = await signalsWrite("DELETE", streamPath(props.stream.stream_id), { reason: reason.trim(), mfa_code: code });
        return r.ok ? { ok: true as const, body: r.body } : { ok: false as const, code: r.code };
      },
      () => props.onDone(),
      () => setCode(""),
    );
  };
  return (
    <Modal
      open
      onClose={busy ? () => undefined : props.onClose}
      role="alertdialog"
      title="Delete stream"
      description={`Delete the stream to ${props.stream.receiver_host ?? "this receiver"}? Signals already queued for it are dropped.`}
      footer={
        <Footer
          formId={formId}
          busy={busy}
          disabled={reason.trim().length === 0 || code.length !== 6}
          label="Delete stream"
          busyLabel="Deleting…"
          danger
          onClose={props.onClose}
        />
      }
    >
      <form id={formId} className="svc-admin__form" onSubmit={submit} noValidate>
        <ReasonAndCode reason={reason} setReason={setReason} code={code} setCode={setCode} error={error} />
      </form>
    </Modal>
  );
}
function ReceiverModal(props: {
  readonly current: ReceiverView | null;
  readonly onClose: () => void;
  readonly onDone: (message: string) => Promise<void>;
}): ReactNode {
  const formId = useId();
  const issuerId = useId();
  const jwksId = useId();
  const audienceId = useId();
  const bearerId = useId();
  const { current } = props;
  const [issuer, setIssuer] = useState(current?.issuer ?? "");
  const [jwks, setJwks] = useState(current?.jwks_uri ?? "");
  const [audience, setAudience] = useState(current?.audience ?? "");
  const [enabled, setEnabled] = useState(current?.enabled ?? true);
  const [otherHost, setOtherHost] = useState(current?.jwks_other_host ?? false);
  const [rotate, setRotate] = useState(false);
  const [reason, setReason] = useState("");
  const [code, setCode] = useState("");
  const [bearer, setBearer] = useState<string | null>(null);
  const [receiveUrl, setReceiveUrl] = useState<string | null>(null);
  const { busy, error, run } = useSignalsSubmit();
  const submit = (e: FormEvent): void => {
    e.preventDefault();
    void run(
      async () => {
        const r = await signalsWrite<{ bearer?: string; receiver: ReceiverView }>("PUT", RECEIVER_PATH, {
          issuer: issuer.trim(),
          jwks_uri: jwks.trim(),
          audience: audience.trim(),
          enabled,
          jwks_other_host: otherHost,
          rotate_bearer: rotate,
          reason: reason.trim(),
          mfa_code: code,
        });
        return r.ok ? { ok: true as const, body: r.body } : { ok: false as const, code: r.code };
      },
      async (body) => {
        if (typeof body.bearer === "string") {
          // Shown ONCE: only its hash is stored, so it cannot be shown again.
          setBearer(body.bearer);
          setReceiveUrl(body.receiver.receive_url);
          return;
        }
        await props.onDone("Receiver saved.");
      },
      () => setCode(""),
    );
  };
  if (bearer !== null) {
    const finish = (): void => {
      void props.onDone("Receiver saved. The bearer secret was shown once.");
    };
    return (
      <Modal
        open
        onClose={finish}
        title="Receiver saved"
        description="Give the receive URL and this bearer secret to your identity provider. The secret is shown only now; only its hash is stored."
        footer={<button type="button" className="svc-admin__btn" onClick={finish}>I have copied it</button>}
      >
        <div className="svc-admin__field">
          <label htmlFor={bearerId}>Bearer secret</label>
          <RevealInput id={bearerId} className="svc-admin__input" value={bearer} onChange={() => undefined} ariaLabel="Bearer secret" />
        </div>
        <p>Receive URL: <code>{receiveUrl}</code></p>
      </Modal>
    );
  }
  return (
    <Modal
      open
      onClose={busy ? () => undefined : props.onClose}
      title={current === null ? "Configure receiver" : "Edit receiver"}
      description="Accept account-disabled and session-revoked signals from your identity provider. They deprovision that provider's users' agents in this team only."
      footer={
        <Footer
          formId={formId}
          busy={busy}
          disabled={issuer.trim() === "" || jwks.trim() === "" || audience.trim() === "" || reason.trim().length === 0 || code.length !== 6}
          label="Save receiver"
          busyLabel="Saving…"
          onClose={props.onClose}
        />
      }
    >
      <form id={formId} className="svc-admin__form" onSubmit={submit} noValidate>
        <div className="svc-admin__field">
          <label htmlFor={issuerId}>Issuer (https)</label>
          <input id={issuerId} type="url" value={issuer} required onChange={(e) => setIssuer(e.target.value)} />
        </div>
        <div className="svc-admin__field">
          <label htmlFor={jwksId}>Key set URL (https)</label>
          <input id={jwksId} type="url" value={jwks} required onChange={(e) => setJwks(e.target.value)} />
        </div>
        <label className="svc-admin__check">
          <input type="checkbox" checked={otherHost} onChange={(e) => setOtherHost(e.target.checked)} /> The key set is on a different host than the issuer (recorded in the audit trail)
        </label>
        <div className="svc-admin__field">
          <label htmlFor={audienceId}>Audience your provider puts in <code>aud</code></label>
          <input id={audienceId} type="text" value={audience} required onChange={(e) => setAudience(e.target.value)} />
        </div>
        <label className="svc-admin__check">
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} /> Receiving enabled
        </label>
        {current !== null && (
          <label className="svc-admin__check">
            <input type="checkbox" checked={rotate} onChange={(e) => setRotate(e.target.checked)} /> Rotate the bearer secret (the old one stops working at once)
          </label>
        )}
        <ReasonAndCode reason={reason} setReason={setReason} code={code} setCode={setCode} error={error} />
      </form>
    </Modal>
  );
}
